import { screen } from '@testing-library/react';
import { userEvent } from '@testing-library/user-event';
import { createRef } from 'react';
import { describe, expect, it, vi } from 'vitest';

import { render } from '@dynatrace/strato-components-testing/vitest';

import { RunQueryButton } from './RunQueryButton.js';

/** Returns the RunQueryButton element */
const getButton = () => screen.getByRole('button');

describe('RunQueryButton', () => {
  /** These tests can also be covered by the visual regression tests*/
  describe('variants', () => {
    it('should be cancelable by default', () => {
      const rendered = render(<RunQueryButton queryState="loading" />);
      const text = rendered.queryByText('Cancel query', { exact: false });
      expect(text).toBeTruthy();
    });

    it('can be set to non-cancelable', () => {
      const rendered = render(
        <RunQueryButton queryState="loading" cancelable={false} />,
      );
      const text = rendered.queryByText('(click to cancel)', { exact: false });
      expect(text).toBeFalsy();
    });

    it('should trigger the click handler normally for non-loading states', async () => {
      const onClick = vi.fn();
      const rendered = render(
        <RunQueryButton queryState="idle" onClick={onClick} />,
      );
      const button = getButton();
      await userEvent.click(button);
      expect(onClick).toHaveBeenCalledTimes(1);

      rendered.rerender(
        <RunQueryButton queryState="success" onClick={onClick} />,
      );
      await userEvent.click(button);
      expect(onClick).toHaveBeenCalledTimes(2);

      rendered.rerender(
        <RunQueryButton queryState="error" onClick={onClick} />,
      );
      await userEvent.click(button);
      expect(onClick).toHaveBeenCalledTimes(3);
    });

    it('should trigger the click handler for loading state if it is cancelable', async () => {
      const onClick = vi.fn();
      const rendered = render(
        <RunQueryButton queryState="loading" onClick={onClick} />,
      );
      const button = getButton();
      await userEvent.click(button);
      expect(onClick).toHaveBeenCalledTimes(1);

      rendered.rerender(
        <RunQueryButton
          queryState="loading"
          cancelable={false}
          onClick={onClick}
        />,
      );
      await userEvent.click(button);
      // Click count should not have changed.
      expect(onClick).toHaveBeenCalledTimes(1);
    });
  });

  it('should set data-dtrum on the root level of the component', () => {
    render(
      <>
        <RunQueryButton
          queryState="loading"
          cancelable={false}
          data-dtrum-mask
          data-dtrum-allow
          data-testid="dtrum"
        />
        <RunQueryButton data-testid="no-dtrum" cancelable={false} />
      </>,
    );

    expect(screen.getByTestId('dtrum')).toHaveAttribute('data-dtrum-mask');
    expect(screen.getByTestId('dtrum')).toHaveAttribute('data-dtrum-allow');
    expect(screen.getByTestId('no-dtrum')).not.toHaveAttribute(
      'data-dtrum-mask',
    );
    expect(screen.getByTestId('no-dtrum')).not.toHaveAttribute(
      'data-dtrum-allow',
    );
  });

  describe('forwardRef', () => {
    it('should forward the ref to the underlying button component', () => {
      const ref = createRef<HTMLButtonElement>();
      render(<RunQueryButton ref={ref} />);

      expect(getButton()).toEqual(ref.current);
    });
  });

  describe('behavior tracking attributes', () => {
    it('should add data-dt-* attributes to the button', () => {
      render(
        <RunQueryButton
          data-dt-content="tracking"
          data-dt-name="MyRunQueryButton"
          data-dt-children-name="RunQueryChildren"
        />,
      );

      const button = getButton();
      expect(button).toHaveAttribute('data-dt-content', 'tracking');
      expect(button).toHaveAttribute('data-dt-name', 'MyRunQueryButton');
      expect(button).toHaveAttribute(
        'data-dt-children-name',
        'RunQueryChildren',
      );
    });

    describe('data-dt-component', () => {
      it('should not override the data-dt-component attribute', async () => {
        const componentName = 'RunQueryButtonComponent';
        render(<RunQueryButton data-dt-component={componentName} />);
        const iconButton = await screen.findByRole('button');
        expect(iconButton).toHaveAttribute(
          'data-dt-component',
          'RunQueryButton',
        );
      });
    });
  });

  describe('loading a11y', () => {
    it('should set aria-busy when queryState is loading', () => {
      render(<RunQueryButton queryState="loading" />);
      expect(getButton()).toHaveAttribute('aria-busy', 'true');
    });

    it.each(['idle', 'success', 'error'] as const)(
      'should not set aria-busy when queryState is %s',
      (queryState) => {
        render(<RunQueryButton queryState={queryState} />);
        expect(getButton()).toHaveAttribute('aria-busy', 'false');
      },
    );

    it('should set aria-disabled when loading and not cancelable', () => {
      render(<RunQueryButton queryState="loading" cancelable={false} />);
      expect(getButton()).toHaveAttribute('aria-disabled', 'true');
    });

    it('should not set aria-disabled when loading and cancelable', () => {
      render(<RunQueryButton queryState="loading" cancelable />);
      expect(getButton()).toHaveAttribute('aria-disabled', 'false');
    });

    it.each(['idle', 'success', 'error'] as const)(
      'should not set aria-disabled when queryState is %s',
      (queryState) => {
        render(<RunQueryButton queryState={queryState} />);
        expect(getButton()).toHaveAttribute('aria-disabled', 'false');
      },
    );

    it('should render a live region with the button label for each state', () => {
      const { rerender } = render(<RunQueryButton queryState="idle" />);
      expect(screen.getByRole('status')).toHaveTextContent('Run query');

      rerender(<RunQueryButton queryState="loading" />);
      expect(screen.getByRole('status')).toHaveTextContent('Cancel query');

      rerender(<RunQueryButton queryState="loading" cancelable={false} />);
      expect(screen.getByRole('status')).toHaveTextContent('Running');

      rerender(<RunQueryButton queryState="error" />);
      expect(screen.getByRole('status')).toHaveTextContent(
        'Error (click to retry)',
      );
    });
  });

  describe('className & style', () => {
    it('should add consumer class names and styles', () => {
      render(
        <RunQueryButton
          className="test"
          style={{ backgroundColor: 'khaki' }}
        />,
      );

      const root = getButton();
      expect(root).toHaveClass('test');
      expect(root).toHaveStyle('background-color: rgb(240, 230, 140)');
    });
  });
});
